<template>
  <section class="security-section">
    <!-- Section Header -->
    <div class="security-header">
      <h3 class="security-title">
        Security & <i>Your Responsibility</i>
      </h3>
      <p class="security-description">
        We use bank-grade encryption and proven blockchain technology. But with great power comes great responsibility — here's what keeps your funds safe and what you need to protect.
      </p>
    </div>

    <!-- Security Features -->
    <div class="security-content">
      <!-- What Protects You -->
      <div class="security-block">
        <h4 class="security-block-title">
          What protects you
        </h4>

        <div class="security-cards">
          <div
            v-for="feature in securityFeatures"
            :key="feature.id"
            class="security-card security-card-safe"
          >
            <div class="security-card-icon">{{ feature.icon }}</div>
            <div class="security-card-content">
              <h5 class="security-card-title">{{ feature.title }}</h5>
              <p class="security-card-description">{{ feature.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Your Responsibility -->
      <div class="security-block">
        <h4 class="security-block-title security-block-title-warning">
          Your responsibility
        </h4>

        <div class="security-cards">
          <div
            v-for="warning in securityWarnings"
            :key="warning.id"
            class="security-card security-card-warning"
          >
            <div class="security-card-icon">{{ warning.icon }}</div>
            <div class="security-card-content">
              <h5 class="security-card-title">{{ warning.title }}</h5>
              <p class="security-card-description">{{ warning.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Important Notice -->
      <div class="security-notice">
        <div class="security-notice-icon">ℹ️</div>
        <div class="security-notice-content">
          <h5 class="security-notice-title">Important to know</h5>
          <p class="security-notice-text">
            Each gift box is a non-custodial wallet, which means <strong>you are in full control</strong> of your funds. We do not store your private keys or recovery phrases, and we <strong>cannot recover your wallet</strong> if you lose access. Treat your emoji PIN, link and recovery phrase like cash — once lost, it's gone forever.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const securityFeatures = [
  {
    id: 1,
    icon: '🔒',
    title: 'Bank-grade encryption',
    description: 'AES-256-GCM encryption with 500,000 PBKDF2 iterations protects your wallet data'
  },
  {
    id: 2,
    icon: '🔑',
    title: 'Non-custodial architecture',
    description: 'Only you control your funds. Your private keys never leave your device or get sent to any server'
  },
  {
    id: 3,
    icon: '✅',
    title: 'Proven blockchain standards',
    description: 'Built on BIP39, BIP44, and Ethereum standards used by millions of users worldwide'
  },
  {
    id: 4,
    icon: '🛡️',
    title: 'Replay attack protection',
    description: 'Nonce management and deadline enforcement prevent unauthorized repeat transactions'
  }
]

const securityWarnings = [
  {
    id: 1,
    icon: '⚠️',
    title: 'Save your emoji PIN and link',
    description: 'Without the emoji PIN, noone can decrypt the gift link. Share it securely and follow instructions.'
  },
  {
    id: 2,
    icon: '⚠️',
    title: 'Backup your recovery phrase',
    description: 'The 12-word recovery phrase is the LAST way to restore access to the money on a gift card. Store it securely offline.'
  },
  {
    id: 3,
    icon: '⚠️',
    title: 'We cannot help if lost',
    description: 'We do not store your keys or backup phrases. If you lose them, we cannot recover your funds — no one can.'
  }
]
</script>

<style scoped>
.security-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 16px;
  gap: 24px;
  background: #4B71EE;
  border-radius: 24px;
  max-width: 100%;
  overflow: hidden;
}

.security-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  align-self: stretch;
}

.security-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: 0.01em;
  color: #FFFFFF;
  align-self: stretch;
}

.security-description {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: #FFFFFF;
  align-self: stretch;
}

.security-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-self: stretch;
}

.security-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
}

.security-block-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 0.01em;
  color: #FFFFFF;
  padding: 8px 0px;
}

.security-block-title-warning {
  color: #FFE5B4;
}

.security-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
}

.security-card {
  display: flex;
  align-items: flex-start;
  padding: 14px 12px;
  gap: 12px;
  border-radius: 12px;
  align-self: stretch;
}

.security-card-safe {
  background: #FFFFFF;
}

.security-card-warning {
  background: #FFF9F0;
  border: 1px solid #FFE5B4;
}

.security-card-icon {
  font-size: 24px;
  line-height: 24px;
  flex-shrink: 0;
}

.security-card-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.security-card-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.01em;
  color: #182855;
}

.security-card-description {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.005em;
  color: #3A55A6;
}

.security-notice {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
  background: #FFFFFF;
  border-radius: 12px;
  border: 2px solid #4B71EE;
  align-self: stretch;
}

.security-notice-icon {
  font-size: 24px;
  line-height: 24px;
  flex-shrink: 0;
}

.security-notice-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.security-notice-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: #182855;
}

.security-notice-text {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.005em;
  color: #3A55A6;
}

.security-notice-text strong {
  font-weight: 600;
  color: #182855;
}
</style>
